import { marked, Renderer } from 'marked';

/**
 * The single Markdown contract for Chapter SSR and SPA rendering.
 *
 * Raw HTML is rendered as text and only ordinary web/mail links are emitted,
 * so this is safe to use in the Worker without a browser-only sanitizer.
 */
export function renderSafeGfmMarkdown(markdown: string): string {
	if (!markdown) return '';

	const renderer = new Renderer();
	renderer.html = ({ text }) => escapeHtml(text);
	renderer.link = ({ href, title, tokens }) => {
		const text = renderer.parser.parseInline(tokens);
		const url = safeLinkUrl(href);
		if (!url) return text;
		return `<a href="${url}"${title ? ` title="${escapeHtml(title)}"` : ''} rel="noopener">${text}</a>`;
	};
	renderer.image = ({ href, title, text, tokens }) => {
		const alt = tokens ? renderer.parser.parseInline(tokens, renderer.parser.textRenderer) : text;
		const url = safeImageUrl(href);
		if (!url) return escapeHtml(alt);
		return `<img src="${url}" alt="${escapeHtml(alt)}"${title ? ` title="${escapeHtml(title)}"` : ''}>`;
	};

	const html = marked.parse(markdown, { gfm: true, breaks: false, async: false, renderer });
	return html.replace(/<pre><code class="language-mermaid">([\s\S]*?)<\/code><\/pre>/g, (_match: string, body: string) => `<div class="mermaid">${body}</div>`);
}

function safeLinkUrl(value: string): string | null {
	const url = value.trim();
	if (!url || url.startsWith('//')) return null;
	if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !/^(?:https?:|mailto:)/i.test(url)) return null;
	return escapeHtml(url);
}

function safeImageUrl(value: string): string | null {
	const url = value.trim();
	if (!url || url.startsWith('//')) return null;
	if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https?:/i.test(url)) return null;
	return escapeHtml(url);
}

function escapeHtml(value: string): string {
	return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
